import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  useColorModeValue,
  Text,
  Button,
  HStack,
  Badge,
  Divider,
  FormControl,
  Textarea,
  IconButton,
  Tooltip,
  useToast,
  Input,
  Select,
  NumberInput,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInputStepper,
  NumberInputField,
  CheckboxGroup,
  Checkbox,
  Stack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PrivateNavbar } from "../../components/layout/PrivateNavbar";
import { Footer } from "../../components/layout/Footer";
import { FiSend, FiArrowLeft } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { AIModel, Character } from "../../types";
import { agentsAPI } from "services/agents-api";
import { charactersAPI } from "services/characters-api";
import { aiAPI } from "services";

export const EditCharacter: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const [character, setCharacter] = useState<Character | null>(null);
  const [arariaModels, setArariaModels] = useState<AIModel[]>([]);
  const loadArariaModels = async () => {
    const arariaModels = await aiAPI.getModels();
    // filter models by capabilities (have to have chat and llm)
    const filteredModels = arariaModels.filter(
      (model) =>
        model.capabilities?.includes("chat") &&
        model.capabilities?.includes("tool-calling") &&
        (model.id?.includes("claude") ||
          model.id?.includes("gemini") ||
          model.id?.includes("gpt")) &&
        model.type === "llm"
    );
    setArariaModels(filteredModels);
  };
  useEffect(() => {
    if (id) {
      loadCharacter();
      loadArariaModels();
    }
  }, [id]);

  const loadCharacter = async () => {
    try {
      const data = await charactersAPI.getCharacter(id!);
      setCharacter(data);
    } catch (error) {
      console.error("Error loading agent:", error);
      toast({
        title: t("agents.error"),
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleUpdateCharacter = async () => {
    try {
      if (character) {
        // remove userId property from agent
        delete character.userId;
        delete character.user;
        console.log(character);
        await charactersAPI.updateCharacter(id!, character);
        toast({
          title: t("characters.updated"),
          status: "success",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error("Error updating character:", error);
      toast({
        title: t("characters.error"),
        status: "error",
        duration: 3000,
      });
    }
  };

  if (!character) {
    return null;
  }

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            {/* Header */}
            <HStack spacing={4}>
              <IconButton
                aria-label="Back to characters"
                icon={<FiArrowLeft />}
                onClick={() => navigate("/characters")}
              />
              <VStack align="start" spacing={2} flex={1}>
                <Heading
                  size="xl"
                  fontFamily="Nasalization"
                  bgGradient="linear(to-r, brand.primary, brand.secondary)"
                  bgClip="text"
                >
                  {character.name}
                </Heading>
                <HStack spacing={4}>
                  <Text color="gray.500">
                    {t("characters.created", {
                      date: new Date(character.createdAt).toLocaleDateString(
                        i18n.language === "pt-BR" ? "pt-BR" : "en-US"
                      ),
                    })}
                  </Text>
                </HStack>
              </VStack>
            </HStack>

            {/* Agent Content */}
            <VStack spacing={6} bg={cardBg} p={8} borderRadius="xl" shadow="md">
              {/* Name */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("characters.name")}</Heading>
                <Input
                  value={character.name}
                  onChange={(e) =>
                    setCharacter({ ...character, name: e.target.value })
                  }
                />
              </VStack>

              {/* Description */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("characters.description")}</Heading>
                <Textarea
                  value={character.description}
                  onChange={(e) =>
                    setCharacter({ ...character, description: e.target.value })
                  }
                />
              </VStack>

              {/* System Prompt */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("characters.systemPrompt")}</Heading>
                <Textarea
                  value={character.systemPrompt}
                  onChange={(e) =>
                    setCharacter({
                      ...character,
                      systemPrompt: e.target.value,
                    })
                  }
                />
              </VStack>

              {/* Model */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("characters.model")}</Heading>
                <Select
                  value={character.modelName}
                  onChange={(e) =>
                    setCharacter({ ...character, modelName: e.target.value })
                  }
                >
                  {arariaModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </Select>
              </VStack>

              {/* Temperature */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("characters.temperature")}</Heading>
                <NumberInput
                  value={character.temperature}
                  onChange={(valueString) => {
                    const value = parseFloat(valueString);
                    if (!isNaN(value) && value >= 0 && value <= 1) {
                      setCharacter({ ...character, temperature: value });
                    } else {
                      setCharacter({ ...character, temperature: 0.5 });
                    }
                  }}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </VStack>

              {/* Tagline */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("characters.tagline")}</Heading>
                <Input
                  value={character.tagline}
                  onChange={(e) =>
                    setCharacter({ ...character, tagline: e.target.value })
                  }
                />
              </VStack>

              {/* Greeting */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("characters.greeting")}</Heading>
                <Input
                  value={character.greeting}
                  onChange={(e) =>
                    setCharacter({ ...character, greeting: e.target.value })
                  }
                />
              </VStack>
            </VStack>
          </VStack>
          <Button onClick={handleUpdateCharacter}>
            {t("characters.update")}
          </Button>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
